// src/features/payment/RoutesTab.tsx
// Smart-routing rules: map transaction criteria to a provider.
// Covers: ListPaymentRoutes, CreatePaymentRoute, GetPaymentRoute (via list item),
// UpdatePaymentRoute, DeletePaymentRoute.

import { useEffect, useRef, useState } from 'react';
import { observer } from '@react';
import { usePaymentPaymentRoute, usePaymentPaymentProvider, useI18n } from '@react/hooks';
import {
  Button,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  Badge,
  ErrorAlert,
} from '@react/components';
import { Select } from '@/components/Select';
import { FormDialog, FormRow, TitleInput, AutoGrowTextarea } from '@/components/form';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import type { PaymentRoute } from '@core/types/payment';
import { errMessage } from './format';

interface RouteForm {
  name: string;
  description: string;
  provider_id: string;
  priority: string;
  weight: string;
  is_active: boolean;
}

const EMPTY: RouteForm = { name: '', description: '', provider_id: '', priority: '10', weight: '100', is_active: true };

export const RoutesTab = observer(() => {
  const store = usePaymentPaymentRoute();
  const providerStore = usePaymentPaymentProvider();
  const { t } = useI18n();
  const [editing, setEditing] = useState<PaymentRoute | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<RouteForm>(EMPTY);
  const [deleteTarget, setDeleteTarget] = useState<PaymentRoute | null>(null);
  const [error, setError] = useState<string | null>(null);
  const initialFormRef = useRef<RouteForm>(EMPTY);
  const descRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    store.ListPaymentRoutes({ pagination: { cursor: '', limit: 50, direction: 'forward' }, sort: [], date_range: {} as any, filters: {} as any } as any);
    if (providerStore.items.length === 0) {
      providerStore.ListPaymentProviders({ pagination: { cursor: '', limit: 100, direction: 'forward' }, sort: [], date_range: {} as any, filters: {} as any } as any);
    }
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    initialFormRef.current = EMPTY;
    setError(null);
    setFormOpen(true);
  };
  const openEdit = (r: PaymentRoute) => {
    const next: RouteForm = {
      name: r.name ?? '', description: r.description ?? '', provider_id: r.provider?.id ?? '',
      priority: String(r.priority ?? 10), weight: String(r.weight ?? 100), is_active: r.is_active ?? true,
    };
    setEditing(r);
    setForm(next);
    initialFormRef.current = next;
    setError(null);
    setFormOpen(true);
  };

  const canSubmit = !!form.name.trim() && !!form.provider_id.trim();
  const dirty = JSON.stringify(form) !== JSON.stringify(initialFormRef.current);

  const submit = async () => {
    if (!canSubmit) return;
    setError(null);
    try {
      const payload = {
        entity: editing ? (editing.entity as any) : ({} as any),
        name: form.name,
        description: form.description,
        provider: { id: form.provider_id } as any,
        priority: Number(form.priority) || 0,
        weight: Number(form.weight) || 0,
        is_active: form.is_active,
        conditions: { conditions: [] },
        metadata: {},
      };
      if (editing?.entity?.id) {
        await store.UpdatePaymentRoute(editing.entity.id, payload as any);
      } else {
        await store.CreatePaymentRoute(payload as any);
      }
      setFormOpen(false);
    } catch (err) {
      setError(errMessage(err, t('payment.routes.saveError')));
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?.entity?.id) return;
    await store.DeletePaymentRoute(deleteTarget.entity.id);
    setDeleteTarget(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{t('payment.routes.subtitle')}</p>
        <Button size="sm" onClick={openCreate}>{t('payment.routes.create')}</Button>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      <Table striped hoverable>
        <TableHeader>
          <TableRow>
            <TableHead>{t('common.name')}</TableHead>
            <TableHead>{t('payment.routes.provider')}</TableHead>
            <TableHead>{t('payment.routes.priority')}</TableHead>
            <TableHead>{t('payment.routes.weight')}</TableHead>
            <TableHead>{t('common.status')}</TableHead>
            <TableHead className="text-right">{t('common.actions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {store.isLoading ? (
            <TableRow><TableCell colSpan={6} className="text-center py-8">{t('common.loading')}</TableCell></TableRow>
          ) : store.items.length === 0 ? (
            <TableEmpty colSpan={6} message={t('payment.routes.empty')} />
          ) : (
            store.items.map((r) => (
              <TableRow key={r.entity?.id}>
                <TableCell>{r.name}</TableCell>
                <TableCell>{providerStore.items.find((p) => p.entity?.id === r.provider?.id)?.display_name ?? r.provider?.id ?? '--'}</TableCell>
                <TableCell>{r.priority}</TableCell>
                <TableCell>{r.weight}</TableCell>
                <TableCell>{r.is_active ? <Badge variant="success" size="sm">{t('common.active')}</Badge> : <Badge variant="secondary" size="sm">{t('common.inactive')}</Badge>}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(r)}>{t('common.edit')}</Button>
                    <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(r)}>{t('common.remove')}</Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <FormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSubmit={submit}
        submitLabel={editing ? t('common.saveChanges') : t('payment.routes.create')}
        canSubmit={canSubmit}
        isSubmitting={store.isSaving}
        dirty={dirty}
        aria-label={editing ? t('payment.routes.edit') : t('payment.routes.create')}
      >
        <div className="space-y-2 px-3.5 pb-4 pt-2">
          {error && <p className="text-sm text-destructive">{error}</p>}

          <TitleInput
            value={form.name}
            onChange={(v) => setForm((p) => ({ ...p, name: v }))}
            placeholder={t('payment.routes.namePlaceholder')}
            initialFocus
            onEnter={() => descRef.current?.focus()}
            aria-label={t('common.name')}
          />
          <AutoGrowTextarea
            ref={descRef}
            value={form.description}
            onChange={(v) => setForm((p) => ({ ...p, description: v }))}
            placeholder={t('payment.providers.descriptionPlaceholder')}
            minRows={1}
          />

          <div className="space-y-0.5 border-t border-border pt-2">
            <FormRow label={t('payment.routes.provider')} labelWidth="sm">
              <Select
                value={form.provider_id}
                onChange={(e) => setForm((p) => ({ ...p, provider_id: e.target.value }))}
                fullWidth={false}
                className="w-full"
                aria-label={t('payment.routes.provider')}
              >
                <option value="">{t('common.select')}</option>
                {providerStore.items.map((p) => (
                  <option key={p.entity?.id} value={p.entity?.id}>{p.display_name || p.name}</option>
                ))}
              </Select>
            </FormRow>
            <FormRow label={t('payment.routes.priority')} labelWidth="sm">
              <input
                value={form.priority}
                onChange={(e) => setForm((p) => ({ ...p, priority: e.target.value }))}
                inputMode="numeric"
                placeholder="10"
                className="w-full rounded-md border border-input bg-background px-2.5 py-1 text-right text-sm tabular-nums text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </FormRow>
            <FormRow label={t('payment.routes.weight')} labelWidth="sm">
              <input
                value={form.weight}
                onChange={(e) => setForm((p) => ({ ...p, weight: e.target.value }))}
                inputMode="numeric"
                placeholder="100"
                className="w-full rounded-md border border-input bg-background px-2.5 py-1 text-right text-sm tabular-nums text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </FormRow>
            <div className="pt-1">
              <ToggleSwitch label={t('common.active')} checked={form.is_active} onChange={(v) => setForm((p) => ({ ...p, is_active: v }))} />
            </div>
          </div>
        </div>
      </FormDialog>

      <ConfirmDialog
        open={!!deleteTarget}
        title={t('payment.routes.deleteConfirmTitle')}
        destructive
        isConfirming={store.isDeleting}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
});

export default RoutesTab;
